import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import {
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { EMPTY, expand, last, switchMap, timer } from 'rxjs';
import { PageHeaderComponent } from '../../components/page-header/page-header.component';
import { LABELS } from '../../constants/labels.const';
import { OpenDocumentResponse } from '../../models/evidence.model';
import { EvidenceApiService } from '../../services/evidence-api.service';

export const OPEN_POLL_INTERVAL_MS = 2000;
export const OPEN_MAX_ATTEMPTS = 30;
export const OPENED_HISTORY_KEY = 'evidenceDocumentOpened';

export type OpenDocumentState =
  | 'preparing'
  | 'opened'
  | 'unavailable'
  | 'timed-out'
  | 'rate-limited'
  | 'failed';

/**
 * The tab a document opens in. It is a page of this app so that the tab can
 * be opened synchronously by a plain link, then replaced by the file once the
 * API has produced a URL. AgriTrace files answer 202 until prepared, so the
 * same open route is polled; the backend reuses the in-flight job.
 *
 * Once the document is opened the history entry is marked, so returning to
 * this tab with Back or a reload shows the opened state instead of opening the
 * document again; the back-forward cache cannot be relied on to keep the page.
 */
@Component({
  selector: 'ev-open-document',
  imports: [PageHeaderComponent, RouterLink],
  templateUrl: './open-document.component.html',
  styleUrl: './open-document.component.scss'
})
export class OpenDocumentComponent implements OnInit {
  readonly token = input.required<string>();
  readonly itemId = input.required<string>();

  readonly state = signal<OpenDocumentState>('preparing');
  readonly labels = LABELS.open;

  private readonly api = inject(EvidenceApiService);
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    if (this.openedInThisHistoryEntry()) {
      this.state.set('opened');
      return;
    }
    this.prepare();
  }

  prepare(): void {
    this.state.set('preparing');
    this.api
      .open(this.token(), this.itemId())
      .pipe(
        expand((response, index) =>
          response.status === 202 && index < OPEN_MAX_ATTEMPTS - 1
            ? timer(OPEN_POLL_INTERVAL_MS).pipe(
                switchMap(() => this.api.open(this.token(), this.itemId()))
              )
            : EMPTY
        ),
        last(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (response) => this.onResponse(response),
        error: (error: unknown) => this.onError(error)
      });
  }

  /** Asks for a fresh URL, since the one handed out earlier lives only 15 minutes. */
  openAgain(): void {
    this.prepare();
  }

  /** The proxy form works from mainland China as well as elsewhere, so it is the one opened. */
  private onResponse(response: HttpResponse<OpenDocumentResponse>): void {
    const body = response.body;
    const url =
      response.status === 200 && body?.status === 'ready'
        ? (body.proxy_url ?? body.url)
        : null;
    if (!url) {
      this.state.set(response.status === 202 ? 'timed-out' : 'failed');
      return;
    }
    this.state.set('opened');
    this.markOpened();
    this.navigateTo(url);
  }

  private onError(error: unknown): void {
    const status = error instanceof HttpErrorResponse ? error.status : 0;
    if (status === 404) {
      this.state.set('unavailable');
    } else if (status === 429) {
      this.state.set('rate-limited');
    } else {
      this.state.set('failed');
    }
  }

  private markOpened(): void {
    const current = (history.state ?? {}) as Record<string, unknown>;
    history.replaceState({ ...current, [OPENED_HISTORY_KEY]: true }, '');
  }

  private openedInThisHistoryEntry(): boolean {
    const current = history.state as Record<string, unknown> | null;
    return current?.[OPENED_HISTORY_KEY] === true;
  }

  /** `assign` keeps this page in history so Back returns to it. */
  protected navigateTo(url: string): void {
    window.location.assign(url);
  }
}
